import React, {useContext, useEffect, useMemo, useState} from 'react';
import BlockWrapper from 'app/Components/Shared/BlockWrapper';
import TheCounterContext, {categoryFilterFunc} from 'app/Components/Page/TheCounterPage/TheCounterContext';
import ButtonsList from './ButtonsList';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/swiper-bundle.min.css';

const SelfActionButtons = (props) => {
  const [selectedCategory] = useContext(TheCounterContext) ?? [null];

  console.log('SelfActionButtons render', {selectedCategory});
  const {operationBoxItems, backgroundColorDesMobile, buttonLink, buttonLinkTitle, containerWidth, isDisplayTabs, tabList} = props.data;

  const [links, setLinks] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [expandedTabs, setExpandedTabs] = useState({});

  useEffect(() => {
    if (!operationBoxItems?.expandedValue) {
      setLinks(null);
      setTabs([]);
      setActiveTabId(null);
      return;
    }

    // Build tabs directly from EPI TabList and keep EPI order
    const tabsList = (tabList?.expandedValue ?? [])
      .map((tab) => {
        const tabId = tab?.id?.value ?? tab?.categoryId?.value;
        const tabName = tab?.name?.value ?? tab?.title?.value;
        const numberOfItemsToDisplay = tab?.numberOfItemsToDisplay?.value ?? null;
        const numberOfItemsPerRow = tab?.numberOfItemsPerRow?.value ?? null;

        if (tabId == null) return null;

        return {
          id: tabId,
          name: tabName || `קטגוריה ${tabId}`,
          numberOfItemsToDisplay,
          numberOfItemsPerRow,
        };
      })
      .filter(Boolean);

    const items = operationBoxItems.expandedValue
      .map((item, idx) => ({item, idx}))
      .filter(({item}) => categoryFilterFunc(item, selectedCategory))
      .map(({item, idx}) => {
        const {link, linkTitle, itemPriority, showBadge, badgeTitle, badgeColor, relatedCategory} = item;
        const itemCategoryIds = (relatedCategory?.expandedValue ?? [])
          .map((category) => category?.id?.value)
          .filter((categoryId) => categoryId != null);

        return {
          idx,
          title: linkTitle.value,
          href: link.value,
          priority: itemPriority.value,
          showBadge: showBadge.value,
          badgeTitle: badgeTitle.value,
          badgeColor: badgeColor?.value,
          categoryIds: itemCategoryIds,
        };
      });

    setLinks(items);
    setTabs(tabsList);
    setExpandedTabs((prev) => {
      const nextExpandedTabs = {};

      tabsList.forEach((tab) => {
        if (prev[tab.id]) {
          nextExpandedTabs[tab.id] = true;
        }
      });

      return nextExpandedTabs;
    });
    setActiveTabId((prev) => {
      if (!tabsList.length) return null;
      if (prev != null && tabsList.some((tab) => tab.id === prev)) return prev;
      return tabsList[0].id;
    });
  }, [operationBoxItems, selectedCategory, buttonLink, buttonLinkTitle, tabList]);

  const shouldDisplayTabs = Boolean(isDisplayTabs?.value);
  const activeTab = useMemo(() => tabs.find((tab) => tab.id === activeTabId) ?? null, [tabs, activeTabId]);
  const activeItemsPerRow = useMemo(() => {
    const itemsPerRow = Number(activeTab?.numberOfItemsPerRow);
    return Number.isFinite(itemsPerRow) && itemsPerRow > 1 ? itemsPerRow : null;
  }, [activeTab]);

  const onTabClick = (tabId) => {
    setActiveTabId((prevActiveTabId) => {
      if (prevActiveTabId !== tabId) {
        setExpandedTabs({});
      }

      return tabId;
    });
  };

  const filteredLinks = useMemo(() => {
    if (!links) return null;

    const visibleLinks = !shouldDisplayTabs || activeTabId == null ? links : links.filter((link) => link.categoryIds?.includes(activeTabId));
    const allActionsTitle = buttonLinkTitle?.value;
    const allActionsHref = buttonLink?.value;
    const hasAllActionsButton = Boolean(allActionsTitle);

    if (!shouldDisplayTabs) {
      if (hasAllActionsButton && allActionsHref) {
        return [
          ...visibleLinks,
          {
            idx: 'all-actions-link',
            title: allActionsTitle,
            href: allActionsHref,
            priority: null,
            showBadge: false,
            badgeTitle: '',
            isAllActionsButton: true,
            categoryIds: [],
          },
        ];
      }

      return visibleLinks;
    }

    const itemsLimit = Number(activeTab?.numberOfItemsToDisplay);
    const hasItemsLimit = Number.isFinite(itemsLimit) && itemsLimit > 0;
    const isExpanded = Boolean(activeTab && expandedTabs[activeTab.id]);

    if (!hasItemsLimit) {
      if (hasAllActionsButton && allActionsHref) {
        return [
          ...visibleLinks,
          {
            idx: `${activeTab?.id ?? 'default'}-all-actions-link`,
            title: allActionsTitle,
            href: allActionsHref,
            priority: null,
            showBadge: false,
            badgeTitle: '',
            isAllActionsButton: true,
            categoryIds: [],
          },
        ];
      }

      return visibleLinks;
    }

    if (isExpanded) {
      return visibleLinks;
    }

    const actionButtonTitle = allActionsTitle || 'הצג הכל';
    const shouldShowActionButton = Boolean(allActionsHref) || visibleLinks.length > itemsLimit;

    if (!shouldShowActionButton) {
      return visibleLinks;
    }

    const visibleItemsCount = Math.max(itemsLimit - 1, 0);
    const limitedLinks = visibleLinks.slice(0, visibleItemsCount);

    return [
      ...limitedLinks,
      {
        idx: `${activeTab?.id ?? 'default'}-all-actions`,
        title: actionButtonTitle,
        href: allActionsHref,
        priority: null,
        showBadge: false,
        badgeTitle: '',
        isAllActionsButton: true,
        categoryIds: [],
        onClick: allActionsHref
          ? null
          : () => {
              if (!activeTab?.id) return;

              setExpandedTabs((prev) => ({
                ...prev,
                [activeTab.id]: true,
              }));
            },
      },
    ];
  }, [links, shouldDisplayTabs, activeTabId, activeTab, buttonLink, buttonLinkTitle, expandedTabs]);

  return (
    <div
      className='self-action-buttons-container'
      style={{backgroundColor: backgroundColorDesMobile?.value ? backgroundColorDesMobile?.value : '#f4eefc'}}>
      <BlockWrapper props={{...props, data: {...props.data, buttonLink: undefined, buttonLinkTitle: undefined}}} disableFadeIn>
        <div className='main-buttons-block-container'>
          {shouldDisplayTabs && tabs?.length > 0 && (
            <Swiper      
              className='category-tabs'
              dir='rtl'
              role='tablist'
              aria-label='self-actions-categories'
              slidesPerView='auto'
              spaceBetween={16}
              slidesOffsetBefore={18}
              slidesOffsetAfter={18}
              allowTouchMove={true}   
              freeMode={true}
              breakpoints={{
                769: {
                  spaceBetween: 32,
                  slidesOffsetBefore: 0,
                  slidesOffsetAfter: 0,
                  allowTouchMove: false,
                },
              }}
              >
              {tabs.map((tab) => (
                <SwiperSlide key={tab.id} className={`category-tab-slide ${activeTabId === tab.id ? 'active' : ''}`}>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={activeTabId === tab.id}
                    className={`category-tab ${activeTabId === tab.id ? 'active' : ''}`}
                    onClick={() => onTabClick(tab.id)}>
                    {tab.name}
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
          {filteredLinks ? (
            <ButtonsList
              links={filteredLinks}
              actionCallback={props.actionCallback}
              containerWidth={containerWidth?.value}
              itemsPerRow={shouldDisplayTabs ? activeItemsPerRow : null}></ButtonsList>
          ) : null}
        </div>
      </BlockWrapper>
    </div>
  );
};

export default SelfActionButtons;
